import clsx from 'clsx'
import styles from './controls.module.css'
import HighlightText from 'components/common/HighlightText'

interface CountPillProps {
  label: string
  /** Disjunctive count; `null` when the model has none for this option (no count slot). */
  count: number | null
  selected: boolean
  onClick: () => void
  /** ARIA semantics: a pressable toggle (default) or a radio inside a `radiogroup`
   *  (single-select groups). */
  kind?: 'toggle' | 'radio'
  /** Underline this query inside the label (drawer search hits). */
  highlight?: string
  title?: string
}

/**
 * The one counted chip: label + disjunctive count, blue when selected, faded at
 * zero (nothing left to match) unless it is the selection itself. Every pill in the
 * rail and the drawer renders through here so the fade and markup can't drift.
 */
const CountPill: React.VFC<CountPillProps> = ({
  label,
  count,
  selected,
  onClick,
  kind = 'toggle',
  highlight,
  title,
}) => (
  <button
    type="button"
    role={kind === 'radio' ? 'radio' : undefined}
    className={clsx(styles.pill, {
      [styles.pillActive]: selected,
      [styles.pillZero]: count === 0 && !selected,
    })}
    aria-pressed={kind === 'radio' ? undefined : selected}
    aria-checked={kind === 'radio' ? selected : undefined}
    title={title}
    onClick={onClick}
  >
    {/* One flex item: the pill is inline-flex with a gap, and the highlight
        segments must not each become an item. */}
    <span>
      {highlight ? (
        <HighlightText text={label} query={highlight} highlightClassName={styles.pillHighlight} />
      ) : (
        label
      )}
    </span>
    {count !== null && <span className={styles.pillCount}>{count.toLocaleString('en-US')}</span>}
  </button>
)

export default CountPill
